import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DeclineQuoteModal, SendPaymentLinkModal } from '@/modules/admin/components/AdminModals';
import { getCurrencySymbol } from '@shared/constants/billing.js';
import { captureError } from '@/utils/errorHandler';

export default function AdminPricingSection({ 
  app,
  appId, 
  hasQuote, 
  authFetch,
  showToast,
  onUpdate,
  onSendQuote
}) {
  const appData = app;
  const navigate = useNavigate();
  const location = useLocation();
  const [editingMaintenanceFee, setEditingMaintenanceFee] = useState(false);
  const [maintenanceFee, setMaintenanceFee] = useState(appData?.monthlyMaintenanceFee || '0.00');
  const [savingFee, setSavingFee] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineNote, setDeclineNote] = useState('');
  const [declining, setDeclining] = useState(false);
  const [settingPaymentMethod, setSettingPaymentMethod] = useState(false);
  const [showSendPaymentLinkModal, setShowSendPaymentLinkModal] = useState(false);
  const [paymentLinkAmount, setPaymentLinkAmount] = useState('');
  const [paymentLinkMonthly, setPaymentLinkMonthly] = useState('0.00');
  const [sendingPaymentLink, setSendingPaymentLink] = useState(false);
  const [paymentLinkResult, setPaymentLinkResult] = useState(null);
  const [settingUpSubscription, setSettingUpSubscription] = useState(false);
  
  const depositPaid = appData?.depositPaidAt != null;
  const finalPaid = appData?.finalPaidAt != null;
  const isOnChargesPage = location.pathname.includes('/charges');
  
  // Base path - always use /app/ route (legacy /tool/ routes redirect)
  const basePath = `/admin-portal/app/${appId}`;

  const handleDeclineQuote = () => {
    setDeclineNote('');
    setShowDeclineModal(true);
  };

  const closeDeclineModal = () => {
    setShowDeclineModal(false);
    setDeclineNote('');
  };

  const submitDeclineQuote = async () => {
    setDeclining(true);
    try {
      const response = await authFetch('/api/admin/decline-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId,
          note: declineNote.trim() || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to decline quote');
      }

      showToast('✅ Quote declined successfully', 'success');
      setShowDeclineModal(false);
      setDeclineNote('');
      
      // Reload the app data to reflect the changes
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      captureError(error, {
        action: 'handleDeclineQuote',
        component: 'AdminPricingSection',
        extra: { appId, declineNote },
      });
      console.error('Error declining quote:', error);
      showToast(error.message || 'Failed to decline quote', 'error');
    } finally {
      setDeclining(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">💰 Pricing & Billing</h2>
      
      {/* Payment Method */}
      {appData?.selectedPaymentMethod && (
        <div className="mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Payment Method:</span>
            <span className="text-sm font-semibold text-gray-900">💳 Quote</span>
          </div>
        </div>
      )}

      {/* User's Stated Budget - Show when quote payment method and user provided budget */}
      {appData?.selectedPaymentMethod === 'quote' && (appData?.userBudgetBuild || appData?.userBudgetMonthly) && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm font-semibold text-amber-800 mb-2">💰 User's Stated Budget</p>
          <div className="space-y-1">
            {appData?.userBudgetBuild && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-amber-700">Build (one-time):</span>
                <span className="font-semibold text-amber-900">
                  {getCurrencySymbol(appData?.preferredCurrency || 'usd')}{parseFloat(appData.userBudgetBuild).toFixed(2)}
                </span>
              </div>
            )}
            {appData?.userBudgetMonthly && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-amber-700">Monthly maintenance:</span>
                <span className="font-semibold text-amber-900">
                  {getCurrencySymbol(appData?.preferredCurrency || 'usd')}{parseFloat(appData.userBudgetMonthly).toFixed(2)}/month
                </span>
              </div>
            )}
          </div>
          <p className="text-xs text-amber-600 mt-2">This is what the user said they would pay when requesting a quote.</p>
        </div>
      )}

      {/* Quote Section - Only show if payment method is quote */}
      {hasQuote && appData?.selectedPaymentMethod === 'quote' ? (
        <>
          {/* Payment Flow Info */}
          {parseFloat(appData?.quotedAmount || 0) === 0 && parseFloat(appData?.monthlyMaintenanceFee || 0) > 0 && !depositPaid && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>{getCurrencySymbol(appData?.preferredCurrency || 'usd')}0 quote with monthly fee:</strong> User needs to set up payment method (no charge now). Once set up, deposit and final payment are auto-marked as paid.
              </p>
            </div>
          )}
          
          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Deposit Status:</span>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                depositPaid ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {depositPaid ? '✅ Paid' : '⏳ Pending Payment'}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Total Quote:</span>
              <span className="text-sm font-semibold text-gray-900">
                {getCurrencySymbol(appData?.preferredCurrency || 'usd')}{parseFloat(appData?.quotedAmount || 0).toFixed(2)}
              </span>
            </div>
            {parseFloat(appData?.depositAmount || 0) > 0 && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Deposit:</span>
                <span className={`text-sm font-semibold ${depositPaid ? 'text-green-600' : 'text-gray-900'}`}>
                  {getCurrencySymbol(appData?.preferredCurrency || 'usd')}{parseFloat(appData.depositAmount).toFixed(2)} {depositPaid && '✓'}
                </span>
              </div>
            )}
            {appData?.finalAmount && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">{parseFloat(appData?.depositAmount || 0) > 0 ? 'Final:' : 'Payment on approval:'}</span>
                <span className={`text-sm font-semibold ${finalPaid ? 'text-green-600' : 'text-gray-900'}`}>
                  {getCurrencySymbol(appData?.preferredCurrency || 'usd')}{parseFloat(appData.finalAmount).toFixed(2)} {finalPaid && '✓'}
                </span>
              </div>
            )}
          </div>

          {/* Monthly Maintenance Fee - Only show if quote exists */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Monthly Maintenance Fee:</label>
              {editingMaintenanceFee ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">{getCurrencySymbol(appData?.preferredCurrency || 'usd')}</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={maintenanceFee}
                    onChange={(e) => setMaintenanceFee(e.target.value)}
                    className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                  <button
                    onClick={async () => {
                      setSavingFee(true);
                      try {
                        const fee = parseFloat(maintenanceFee) || 0;
                        const response = await authFetch('/api/apps/update', {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            id: appId,
                            monthlyMaintenanceFee: fee,
                          }),
                        });
                        if (response.ok) {
                          showToast('✅ Maintenance fee updated', 'success');
                          setEditingMaintenanceFee(false);
                          if (onUpdate) onUpdate();
                        } else {
                          const error = await response.json();
                          throw new Error(error.error || 'Failed to update');
                        }
                      } catch (error) {
                        captureError(error, {
                          action: 'handleUpdateMaintenanceFee',
                          component: 'AdminPricingSection',
                          extra: { appId, maintenanceFee },
                        });
                        showToast(error.message || 'Failed to update maintenance fee', 'error');
                      } finally {
                        setSavingFee(false);
                      }
                    }}
                    disabled={savingFee}
                    className="px-3 py-1 text-xs bg-zapt-purple text-white rounded hover:bg-zapt-purple/90 disabled:opacity-50"
                  >
                    {savingFee ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setMaintenanceFee(appData?.monthlyMaintenanceFee || '0.00');
                      setEditingMaintenanceFee(false);
                    }}
                    className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">
                    {getCurrencySymbol(appData?.preferredCurrency || 'usd')}{parseFloat(appData?.monthlyMaintenanceFee || '0').toFixed(2)}/month
                  </span>
                  {parseFloat(appData?.monthlyMaintenanceFee || '0') === 0 && (
                    <span className="text-xs text-gray-500">(No maintenance fee)</span>
                  )}
                  <button
                    onClick={() => setEditingMaintenanceFee(true)}
                    className="text-xs text-zapt-purple hover:text-zapt-purple/80 underline"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
            {/* Payment Method Status for Monthly Fees */}
            {parseFloat(appData?.monthlyMaintenanceFee || '0') > 0 && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">Payment Method Status:</span>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    appData?.hasMonthlyPaymentMethod 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {appData?.hasMonthlyPaymentMethod ? '✅ Set Up' : '⏳ Not Set Up'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {!appData?.hasMonthlyPaymentMethod ? (
                    <>Customer needs to set up a payment method in their account settings for automatic monthly billing.</>
                  ) : appData?.stripeSubscriptionItemId ? (
                    <>Stripe subscription active. Monthly billing is managed by Stripe.</>
                  ) : (
                    <>Payment method is configured. Subscription billing will be set up when you click below.</>
                  )}
                  <span className="block mt-1 text-gray-400">
                    Note: Subscriptions are per-user (covering all their apps), not per-app.
                  </span>
                </p>
                {appData?.hasMonthlyPaymentMethod && !appData?.stripeSubscriptionItemId && (
                  <button
                    onClick={async () => {
                      setSettingUpSubscription(true);
                      try {
                        const response = await authFetch('/api/admin/set-up-subscription', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ appId }),
                        });
                        if (!response.ok) {
                          const err = await response.json();
                          throw new Error(err.error || 'Failed to set up subscription');
                        }
                        showToast('✅ Stripe subscription set up!', 'success');
                        if (onUpdate) onUpdate();
                      } catch (error) {
                        captureError(error, {
                          action: 'setUpSubscription',
                          component: 'AdminPricingSection',
                          extra: { appId },
                        });
                        showToast(error.message || 'Failed to set up subscription', 'error');
                      } finally {
                        setSettingUpSubscription(false);
                      }
                    }}
                    disabled={settingUpSubscription}
                    className="mt-2 px-3 py-1.5 text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200 rounded hover:bg-amber-200 disabled:opacity-50"
                  >
                    {settingUpSubscription ? 'Setting up...' : 'Set up Stripe subscription'}
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 flex gap-3">
            {!depositPaid && (
              <button
                onClick={() => navigate(`${basePath}/create-quote`)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm"
              >
                Update Quote →
              </button>
            )}
            {!isOnChargesPage && (
              <button
                onClick={() => navigate(`${basePath}/charges`)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm"
              >
                View Charges →
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          {/* No Payment Method Set */}
          {!appData?.selectedPaymentMethod && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
              <p className="text-sm text-gray-700 mb-2">
                <span className="font-semibold text-yellow-700">⚠️ Payment Method Not Set</span>
              </p>
              <p className="text-xs text-gray-600 mb-3">
                No payment method has been configured for this app. Set a payment method so the app can enter the build queue.
              </p>
              <div className="flex gap-2 flex-wrap">
                {!hasQuote && onSendQuote && (
                  <>
                    <button
                      onClick={onSendQuote}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm"
                    >
                      💳 Send Quote →
                    </button>
                    <button
                      onClick={() => {
                        setPaymentLinkAmount(appData?.quotedAmount || '');
                        setPaymentLinkMonthly(appData?.monthlyMaintenanceFee || '0.00');
                        setPaymentLinkResult(null);
                        setShowSendPaymentLinkModal(true);
                      }}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm"
                    >
                      💳 Send Payment Link
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* No Quote Section - Only show when payment method is quote AND no quote has been sent yet */}
          {appData?.selectedPaymentMethod === 'quote' && !hasQuote && (
            <div className="mb-4">
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-600 mb-3">
                  No quote has been sent yet. Send a quote or a direct payment link.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => navigate(`${basePath}/create-quote`)}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm"
                  >
                    Create Quote →
                  </button>
                  <button
                    onClick={() => {
                      setPaymentLinkAmount(appData?.quotedAmount || '');
                      setPaymentLinkMonthly(appData?.monthlyMaintenanceFee || '0.00');
                      setPaymentLinkResult(null);
                      setShowSendPaymentLinkModal(true);
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm"
                  >
                    Send Payment Link
                  </button>
                </div>
              </div>
            </div>
          )}

        </>
      )}

      {/* Decline Quote Modal */}
      <DeclineQuoteModal
        show={showDeclineModal}
        app={appData}
        note={declineNote}
        submitting={declining}
        onClose={closeDeclineModal}
        onSubmit={submitDeclineQuote}
        onNoteChange={setDeclineNote}
      />

      {/* Send Payment Link Modal */}
      <SendPaymentLinkModal
        show={showSendPaymentLinkModal}
        app={appData}
        amount={paymentLinkAmount}
        monthlyFee={paymentLinkMonthly}
        onAmountChange={setPaymentLinkAmount}
        onMonthlyFeeChange={setPaymentLinkMonthly}
        submitting={sendingPaymentLink}
        createdResult={paymentLinkResult}
        onClose={() => {
          setShowSendPaymentLinkModal(false);
          setPaymentLinkResult(null);
          if (onUpdate) onUpdate();
        }}
        onDone={() => {
          setShowSendPaymentLinkModal(false);
          setPaymentLinkResult(null);
          if (onUpdate) onUpdate();
        }}
        onCopyLink={() => showToast('Link copied to clipboard', 'success')}
        onSubmit={async () => {
          setSendingPaymentLink(true);
          try {
            const response = await authFetch('/api/admin/send-payment-link', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                appId,
                amount: parseFloat(paymentLinkAmount) || 0,
                monthlyMaintenanceFee: parseFloat(paymentLinkMonthly) || 0,
              }),
            });
            if (!response.ok) {
              const err = await response.json();
              throw new Error(err.error || 'Failed to send payment link');
            }
            const data = await response.json();
            setPaymentLinkResult(data);
            showToast('✅ Payment link created!', 'success');
          } catch (error) {
            captureError(error, {
              action: 'sendPaymentLink',
              component: 'AdminPricingSection',
              extra: { appId },
            });
            showToast(error.message || 'Failed to send payment link', 'error');
          } finally {
            setSendingPaymentLink(false);
          }
        }}
      />
    </div>
  );
}
